// WebXR. The VR button, two controllers OR two tracked hands, a club in the right hand, and a
// swing: hold the trigger (or pinch), swing, release. The hand's velocity over the last 120 ms
// becomes aim and power — no gesture library, no physics on the club: a swing is a direction and
// a speed, and the server does the rest. What the designer added: a club you can see, a backswing
// trail so you can tell what you just did, a haptic tap when the swing is taken, and a session
// callback so the in-world boards appear only in VR (the DOM HUD is invisible there).
//
// 2026-09-14, after the desk witness (scripts/xr-witness.mjs):
//   - the rig faced a mirror of the aim for any aim off the hole's axis (π - a → π + a);
//   - controller models come from our own /xr-profiles, not jsdelivr, at session start;
//   - `onSwing` also reports the raw speed, so main.ts can scale a putt the way the desk meter does
//     (a 1 m/s tap on the putter used to be sent as a 15 % swing, 1 m/s of ball);
//   - hand tracking: pinch to hold the club, swing, let go. Quest without controllers, Vision Pro.
//
// Contract kept: `setupXR(world, onSwing)` still works; the third argument is optional.

import * as THREE from 'three';
import { VRButton } from 'three/addons/webxr/VRButton.js';
import { XRControllerModelFactory } from 'three/addons/webxr/XRControllerModelFactory.js';
import { XRHandModelFactory } from 'three/addons/webxr/XRHandModelFactory.js';
import type { World } from './scene/world';

interface Track { t: number; p: THREE.Vector3 }

export interface XR {
  update(): void;
  active(): boolean;
  /** Stand the rig behind the ball, facing down the hole. */
  standAt(x: number, y: number, z: number, aimDeg: number): void;
  /** what is holding the club right now */
  inputKind(): 'controllers' | 'hands' | 'none';
}

export interface XROptions {
  /** fires on sessionstart / sessionend; use it to show in-world equivalents of the HUD */
  onSessionChange?(active: boolean): void;
}

/** models for the controllers and the hands are served from our own origin (scripts/vendor-xr-profiles.mjs) */
export const XR_PROFILES_PATH = '/xr-profiles';
export const SESSION_FEATURES = { optionalFeatures: ['local-floor', 'bounded-floor', 'layers', 'hand-tracking'] };

const TRAIL_POINTS = 48;
/** a full swing: the controller speed that means 100 % */
const FULL_SWING_MPS = 5.5;
/** below this the movement was a twitch, not a swing */
const MIN_SWING_MPS = 0.6;

/** A seven iron, roughly: a shaft, a grip, a head. Enough to tell your hands where the club is. */
function makeClub(accent: string): THREE.Group {
  const g = new THREE.Group();
  g.name = 'club';
  const steel = new THREE.MeshStandardMaterial({ color: 0xcfd2cc, roughness: 0.35, metalness: 0.6 });
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.009, 0.88, 8), steel);
  shaft.position.y = -0.44;
  const grip = new THREE.Mesh(
    new THREE.CylinderGeometry(0.014, 0.012, 0.22, 8),
    new THREE.MeshStandardMaterial({ color: 0x1c1f16, roughness: 0.9 }),
  );
  grip.position.y = -0.11;
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.055, 0.022), steel);
  head.position.set(0.03, -0.88, 0);
  head.rotation.z = -0.25;
  const face = new THREE.Mesh(
    new THREE.PlaneGeometry(0.075, 0.04),
    new THREE.MeshStandardMaterial({ color: accent, roughness: 0.6 }),
  );
  face.position.set(0.03, -0.88, 0.012);
  face.rotation.z = -0.25;
  g.add(shaft, grip, head, face);
  // hang it the way a club hangs from a hand holding a controller
  g.rotation.x = Math.PI * 0.18;
  return g;
}

export function setupXR(
  world: World,
  onSwing: (aimDeg: number, power: number, speedMps: number) => void,
  options: XROptions = {},
): XR {
  const { renderer, rig, camera, scene } = world;
  // The VR button only when a headset can actually answer: on a desk or a phone the addon's
  // "VR NOT SUPPORTED" plate sat over the swing hint and said nothing useful (2026-09-13).
  const xrApi = (navigator as Navigator & { xr?: { isSessionSupported(m: string): Promise<boolean> } }).xr;
  if (xrApi) {
    xrApi.isSessionSupported('immersive-vr').then((ok) => { if (ok) document.body.appendChild(VRButton.createButton(renderer, SESSION_FEATURES)); }, () => {});
  }

  const factory = new XRControllerModelFactory();
  factory.setPath(XR_PROFILES_PATH);
  const handFactory = new XRHandModelFactory();
  // the hand mesh loader wants the generic-hand directory itself (it loads left.glb / right.glb from it)
  handFactory.setPath(XR_PROFILES_PATH + '/generic-hand/');
  const tracks = new Map<string, Track[]>();
  const tmp = new THREE.Vector3();
  const clubHead = new THREE.Object3D();
  clubHead.position.set(0.03, -0.88, 0);

  // the backswing: the club head's path while the trigger is held
  const trailGeo = new THREE.BufferGeometry();
  trailGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(TRAIL_POINTS * 3), 3));
  const trail = new THREE.Line(
    trailGeo,
    new THREE.LineBasicMaterial({ color: new THREE.Color(world.theme.accent), transparent: true, opacity: 0.8, depthWrite: false }),
  );
  trail.name = 'swing-trail';
  trail.frustumCulled = false;
  trail.visible = false;
  scene.add(trail);
  let trailCount = 0;

  const club = makeClub(world.theme.accent);
  club.add(clubHead);
  let clubHolder: 'controller' | 'hand' | null = null;
  let kind: 'controllers' | 'hands' | 'none' = 'none';

  /** the end of a hold: the last 120 ms of travel become a shot, or nothing */
  function release(key: string, index: number) {
    const tr = tracks.get(key);
    tracks.delete(key);
    if (key === 'c1' || key === 'h1') trail.visible = false;
    if (!tr || tr.length < 3) return;
    const end = tr[tr.length - 1];
    let start = tr[0];
    for (let k = tr.length - 1; k >= 0; k--) { if (end.t - tr[k].t >= 0.12) { start = tr[k]; break; } }
    const dt = Math.max(0.03, end.t - start.t);
    const vx = (end.p.x - start.p.x) / dt, vz = (end.p.z - start.p.z) / dt;
    const speed = Math.hypot(vx, vz);
    if (speed < MIN_SWING_MPS) return; // a twitch, not a swing
    const power = Math.max(0.15, Math.min(1, speed / FULL_SWING_MPS));
    const aimDeg = (Math.atan2(vx, vz) * 180) / Math.PI;
    pulse(index, power);
    onSwing(aimDeg, power, speed);
  }

  const grips: THREE.Object3D[] = [];
  const hands: THREE.Object3D[] = [];
  for (const i of [0, 1]) {
    const controller = renderer.xr.getController(i);
    rig.add(controller);
    const grip = renderer.xr.getControllerGrip(i);
    grip.add(factory.createControllerModel(grip));
    rig.add(grip);
    grips.push(grip);
    // the left keeps a short ray so the hand is visible
    if (i === 0) {
      const ray = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3(0, 0, -0.4)]),
        new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.5 }),
      );
      controller.add(ray);
    }
    controller.addEventListener('connected', (e) => {
      const src = (e as unknown as { data?: { hand?: unknown } }).data;
      kind = src && src.hand ? 'hands' : 'controllers';
      if (kind === 'controllers' && i === 1 && clubHolder !== 'controller') { grip.add(club); clubHolder = 'controller'; }
    });
    controller.addEventListener('selectstart', () => {
      tracks.set('c' + i, []);
      if (i === 1) { trailCount = 0; trail.visible = true; }
    });
    controller.addEventListener('selectend', () => release('c' + i, i));

    // hand tracking: three's XRHand fires pinchstart / pinchend from the joint distances
    const hand = renderer.xr.getHand(i);
    hand.add(handFactory.createHandModel(hand as unknown as Parameters<XRHandModelFactory['createHandModel']>[0], 'mesh'));
    rig.add(hand);
    hands.push(hand);
    hand.addEventListener('connected', () => { kind = 'hands'; });
    hand.addEventListener('pinchstart', () => {
      tracks.set('h' + i, []);
      if (i === 1) { trailCount = 0; trail.visible = true; }
    });
    hand.addEventListener('pinchend', () => release('h' + i, i));
  }

  /** A tap in the hand at contact, as hard as the swing was. */
  function pulse(index: number, strength: number) {
    const session = renderer.xr.getSession();
    const source = session?.inputSources?.[index] as { gamepad?: { hapticActuators?: { pulse?: (v: number, ms: number) => void }[] } } | undefined;
    source?.gamepad?.hapticActuators?.[0]?.pulse?.(Math.min(1, 0.4 + strength * 0.6), 60);
  }

  /** where a tracked hand is, this frame: its wrist, or nothing if the hand is not seen */
  function handAnchor(i: number): THREE.Object3D | null {
    const joints = (hands[i] as unknown as { joints?: Record<string, THREE.Object3D> }).joints;
    const w = joints && joints['wrist'];
    return w && hands[i].visible ? w : null;
  }

  renderer.xr.addEventListener('sessionstart', () => options.onSessionChange?.(true));
  renderer.xr.addEventListener('sessionend', () => { kind = 'none'; options.onSessionChange?.(false); });

  return {
    active: () => renderer.xr.isPresenting,
    inputKind: () => kind,

    update() {
      if (!renderer.xr.isPresenting) return;
      // the club follows whichever right hand is present: a controller grip, or the wrist joint
      const wrist = handAnchor(1);
      if (wrist && clubHolder !== 'hand') { wrist.add(club); clubHolder = 'hand'; }
      else if (!wrist && clubHolder === 'hand' && kind === 'controllers') { grips[1].add(club); clubHolder = 'controller'; }
      if (tracks.size === 0) return;
      const now = performance.now() / 1000;
      for (const [key, tr] of tracks) {
        const i = key === 'c1' || key === 'h1' ? 1 : 0;
        const source = key.startsWith('h') ? handAnchor(i) : renderer.xr.getController(i);
        if (!source) continue;
        source.getWorldPosition(tmp);
        tr.push({ t: now, p: tmp.clone() });
        while (tr.length > 40) tr.shift();
        if (i !== 1) continue;
        // the club head's own path, not the controller's: that is the line a player recognises
        clubHead.getWorldPosition(tmp);
        const pos = trailGeo.attributes.position as THREE.BufferAttribute;
        if (trailCount < TRAIL_POINTS) {
          pos.setXYZ(trailCount++, tmp.x, tmp.y, tmp.z);
        } else {
          for (let k = 0; k < TRAIL_POINTS - 1; k++) {
            pos.setXYZ(k, pos.getX(k + 1), pos.getY(k + 1), pos.getZ(k + 1));
          }
          pos.setXYZ(TRAIL_POINTS - 1, tmp.x, tmp.y, tmp.z);
        }
        pos.needsUpdate = true;
        trailGeo.setDrawRange(0, trailCount);
      }
    },

    standAt(x, y, z, aimDeg) {
      const a = (aimDeg * Math.PI) / 180;
      rig.position.set(x - Math.sin(a) * 0.7, y, z - Math.cos(a) * 0.7);
      // The headset looks down local -z. A rotation of θ about Y sends (0,0,-1) to (-sin θ, 0, -cos θ),
      // and the aim heading is (sin a, 0, cos a), so θ = π + a. The hand-off had π - a, which is the
      // same thing straight down the hole and a mirror image everywhere else: aim 30° right, face
      // 30° left. Caught by scripts/xr-witness.mjs before it reached a headset (2026-09-14).
      rig.rotation.set(0, Math.PI + a, 0);
      if (!renderer.xr.isPresenting) camera.position.set(0, 1.6, 0);
    },
  };
}
